import React from "react";
import styled from "styled-components";
import { useReactFlow, useStore, useStoreApi } from "reactflow";
import { theme } from "../../../styles/theme";

// Mirrors @reactflow/controls' own internal selector exactly (see
// node_modules/@reactflow/controls/dist/esm/index.js) so "lock canvas" behaves
// identically to React Flow's built-in interactivity toggle — we're only
// replacing the chrome around it, not the underlying behavior.
const isInteractiveSelector = (s: any) => s.nodesDraggable || s.nodesConnectable || s.elementsSelectable;

export function CanvasControls({ toggleMap, setToggleMap }: { toggleMap: boolean, setToggleMap: React.Dispatch<React.SetStateAction<boolean>> }) {
   const { zoomIn, zoomOut, fitView } = useReactFlow();
   const store = useStoreApi();
   const isInteractive = useStore(isInteractiveSelector);

   const toggleInteractivity = () => {
      store.setState({
         nodesDraggable: !isInteractive,
         nodesConnectable: !isInteractive,
         elementsSelectable: !isInteractive,
      });
   };

   return (
      <Main>
         <div className="group">
            <button type="button" title="Zoom in" aria-label="Zoom in" onClick={ () => zoomIn() }>
               <i className="ti ti-plus" aria-hidden="true" />
            </button>
            <div className="divider" aria-hidden="true" />
            <button type="button" title="Zoom out" aria-label="Zoom out" onClick={ () => zoomOut() }>
               <i className="ti ti-minus" aria-hidden="true" />
            </button>
         </div>
         <div className="group">
            <button type="button" title="Fit to screen" aria-label="Fit to screen" onClick={ () => fitView({ duration: 300 }) }>
               <i className="ti ti-maximize" aria-hidden="true" />
            </button>
            <div className="divider" aria-hidden="true" />
            <button
               type="button"
               className={ !isInteractive ? 'active' : '' }
               title="Lock canvas"
               aria-label="Lock canvas"
               onClick={ toggleInteractivity }
            >
               <i className="ti ti-lock" aria-hidden="true" />
            </button>
            <div className="divider" aria-hidden="true" />
            <button
               type="button"
               className={ toggleMap ? 'active' : '' }
               title="Show minimap"
               aria-label="Show minimap"
               onClick={ () => setToggleMap(t => !t) }
            >
               <i className="ti ti-map" aria-hidden="true" />
            </button>
         </div>
      </Main>
   );
}

const Main = styled.div`
   position: relative;
   left: auto;
   bottom: auto;
   z-index: 5;
   display: flex;
   flex-direction: column;
   gap: 10px;
   flex-shrink: 0;

   i.ti {
      display: inline-flex;
      font-size: 16px;
      line-height: 1;
   }

   .group {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 44px;
      padding: 5px;
      background-color: ${theme.surface.groupBg};
      border: 1px solid ${theme.surface.groupBorder};
      border-radius: 16px;
      box-shadow: ${theme.shadows.float};

      .divider {
         width: calc(100% - 8px);
         height: 1px;
         margin: 3px 0;
         background: ${theme.surface.groupBorder};
         flex-shrink: 0;
      }

      button {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 34px;
         height: 34px;
         border: none;
         border-radius: 11px;
         background-color: transparent;
         color: ${theme.colors.textSecondary};
         cursor: pointer;
         transition: background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

         &:hover {
            background-color: ${theme.surface.hoverFill};
            color: ${theme.colors.primary};
         }
         &.active {
            background: ${theme.gradients.primary};
            color: #fff;
            box-shadow: 0 6px 14px -6px rgba(79, 63, 224, 0.5);
         }
      }
   }
`;
